// SOLY-CN-001 mini-scope 04 — pure helpers for the credit-note Mode B preview.
// The authoritative computation lives on the BE (CreateProjectInvoiceHandler).
// This is the FE-side preview that mirrors it so Élodie sees the breakdown live
// while typing. Round AwayFromZero to match decimal.Round AwayFromZero on .NET.

export interface VatSubtotal {
  vatPercent: number
  taxableAmount: number
  vatAmount: number
  totalWithTax: number
}

export interface PreviewLine {
  vatPercent: number
  ttc: number
  ht: number
  vat: number
}

function roundAway(value: number, digits: number): number {
  const factor = Math.pow(10, digits)
  return Math.sign(value) * Math.round(Math.abs(value) * factor) / factor
}

/**
 * Mirror the BE handler normalization: proportion = round(ttc / sourceTtc, 6).
 * Returns 0 when sourceTtc is non-positive — caller should reject before this.
 */
export function normalizeTtcToProportion(partialTtc: number, sourceTtc: number): number {
  if (sourceTtc <= 0) return 0
  return roundAway(partialTtc / sourceTtc, 6)
}

/**
 * Live preview of what Mode B will credit per VAT subtotal. Mirrors the BE
 * domain's per-subtotal computation: subtotalTtc * proportion rounded to 2
 * decimals (cents). Returns positive ttc/ht/vat — the caller renders them as
 * the credited amount (the negative goes into the actual NC TaxTotal in BE).
 */
export function computePreviewByVatRate(
  sourceSubtotals: VatSubtotal[],
  partialTtc: number,
  sourceTtc: number,
): PreviewLine[] {
  if (partialTtc <= 0 || sourceTtc <= 0 || partialTtc >= sourceTtc) return []
  const proportion = normalizeTtcToProportion(partialTtc, sourceTtc)
  return sourceSubtotals
    .filter((s) => s.totalWithTax > 0)
    .map((s) => {
      const ttc = roundAway(s.totalWithTax * proportion, 2)
      const ht = roundAway(ttc / (1 + s.vatPercent / 100), 2)
      const vat = roundAway(ttc - ht, 2)
      return { vatPercent: s.vatPercent, ttc, ht, vat }
    })
    .filter((line) => line.ttc > 0)
}

/**
 * Format a proportion (0..1) as a 2-decimal percent string in the FR-BE
 * convention, e.g. 0.3003 → "30,03 %". Matches the description format the
 * BE writes on the NC InvoiceLine.
 */
export function formatProportion(proportion: number): string {
  const percent = roundAway(proportion * 100, 2)
  return `${percent.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
}

/**
 * Validation matrix for the Mode B partial amount input.
 * Returns null when valid, otherwise a French error message ready for display.
 */
export function validatePartialAmountTtc(partialTtc: number, sourceTtc: number): string | null {
  if (!Number.isFinite(partialTtc) || partialTtc <= 0) {
    return 'Le montant doit être supérieur à 0 €'
  }
  if (sourceTtc <= 0) {
    return 'Le TTC de la facture source doit être positif'
  }
  if (partialTtc >= sourceTtc) {
    return 'Le montant dépasse le TTC de la facture source'
  }
  return null
}
